import React, { useEffect, useState } from 'react';
import { Grid } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useDispatch, useSelector } from 'react-redux';
import { getAlgoEditConfigConfigurations } from '../../../stores/algo-edit-config/getters';
import useAlgoRunning from '../../../hooks/useAlgoRunning';
import useAlgoValidationError from '../../../hooks/useAlgoValidationError';
import ElevatedSelectControlled from '../../../components/Basic/ElevatedSelectControlled';
import { configurationDataUpdate } from '../../../stores/algo-edit-config/actions';
import { useMemo } from 'react';
import {
  INTERVAL_1_MIN,
  RE_ENTRY_REPEAT_SEPARATE,
  RE_ENTRY_REPEAT_COMMON,
  SL_ORDER_PLACEMENT_ADVANCE,
  SL_ORDER_PLACEMENT_LATER,
  ORDER_TYPE_M,
  INTERVAL_ASAP,
} from '@quantiply/common/constants';
import TimeSelectorGeneral from '../../../components/TimeSelectorGeneral';
import useModifyWhileLive from '../../../hooks/useModifyWhileLive';
import useAlgoLoading from '../../../hooks/useAlgoLoading';
import BubbleGridRow from '../../../components/BubbleGridRow';
import BaseLimitOrderTimeoutSeconds from './BaseLimitOrderTimeoutSeconds';
import GridGap from '../../../components/GridGap';

const ReEntryAsapOrderDelayMin = 0;
const ReEntryAsapOrderDelayMax = 600;
export const reEntryIntervalTypeCandleClose = INTERVAL_1_MIN;

const useStyles = makeStyles((theme) => ({
  root: {
    [theme.breakpoints.up('lg')]: {
      marginTop: 8,
    },
  },
  space: {
    marginBottom: 30,
    [theme.breakpoints.up('lg')]: {
      marginBottom: 0,
    },
  },
  ReEntryType: {
    marginBottom: 16,
    [theme.breakpoints.up('lg')]: {
      maxWidth: 240,
      marginRight: 40,
      marginBottom: 0,
    },
  },
  ReEntryOnlyBefore: {
    [theme.breakpoints.up('lg')]: {
      maxWidth: 294,
    },
  },
  ReEntryOnlyAfter: {
    marginBottom: 16,
    [theme.breakpoints.up('lg')]: {
      maxWidth: 294,
      marginRight: 30,
      marginBottom: 0,
    },
  },
  ReEntryMaxCountLegs: {
    marginBottom: 16,
    [theme.breakpoints.up('lg')]: {
      maxWidth: 264,
      marginRight: 40,
      marginBottom: 0,
    },
  },
  ReEntryMaxCountMTM: {
    marginBottom: 16,
    [theme.breakpoints.up('lg')]: {
      maxWidth: 308,
      marginRight: 40,
      marginBottom: 0,
    },
  },
  ReEntryAsapOrderDelay: {
    [theme.breakpoints.up('lg')]: {
      maxWidth: 260,
    },

    '& .MuiInputBase-root': {
      position: 'relative',

      '&::after': {
        display: 'inline-block',
        content: `'min: ${ReEntryAsapOrderDelayMin}; max: ${ReEntryAsapOrderDelayMax} '`,
        position: 'absolute',
        right: 10,
        fontSize: 12,
        color: '#797979',
        letterSpacing: '-0.1px',
        fontStyle: 'italic',
      },
    },
  },
  ReEntryOrderPlacement: {
    marginBottom: 16,
    [theme.breakpoints.up('lg')]: {
      maxWidth: 376,
      marginRight: 40,
    },
  },
  ReEntryActivationType: {
    [theme.breakpoints.up('lg')]: {
      maxWidth: 300,
    },
  },
}));

const ReEntryType = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const algoRunning = useAlgoRunning();
  const loading = useAlgoLoading();
  const reEntryInterval = useSelector((state) => {
    const { reEntryInterval = '' } = getAlgoEditConfigConfigurations(state);

    return reEntryInterval;
  });

  const [showError, helperText] = useAlgoValidationError(
    'configurations.reEntryInterval'
  );

  const onChange = (val) => {
    dispatch(configurationDataUpdate('reEntryInterval', val));
  };

  const options = useMemo(
    () => [
      {
        value: '',
        label: 'Immediate',
      },
      {
        value: reEntryIntervalTypeCandleClose,
        label: 'Candle Close (Stockmock)',
      },
    ],
    []
  );

  return (
    <Grid item xs={12} lg={4} className={classes.ReEntryType}>
      <ElevatedSelectControlled
        label="Re-Execute method:"
        disableClearable
        name="reEntryInterval"
        error={showError}
        helperText={helperText}
        disabled={algoRunning || loading}
        value={reEntryInterval}
        options={options}
        onChange={onChange}
      />
    </Grid>
  );
};

const ReEntryOnlyAfter = () => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const algoRunning = useAlgoRunning();
  const modifyWhileLive = useModifyWhileLive();
  const loading = useAlgoLoading();

  const reEntryAfter = useSelector((state) => {
    const { reEntryAfter = '' } = getAlgoEditConfigConfigurations(state);

    return reEntryAfter;
  });

  const onChange = (val) => {
    dispatch(configurationDataUpdate('reEntryAfter', val));
  };

  const onClear = () => {
    dispatch(configurationDataUpdate('reEntryAfter', ''));
  };

  const [showError, helperText] = useAlgoValidationError(
    'configurations.reEntryAfter'
  );

  return (
    <Grid item xs={12} lg={4} className={classes.ReEntryOnlyAfter}>
      <TimeSelectorGeneral
        label="Re-entry / Re-execute only after (time):"
        name="reEntryAfter"
        value={reEntryAfter}
        error={showError}
        helperText={helperText}
        disabled={(algoRunning && !modifyWhileLive) || loading}
        onChange={onChange}
        onClear={onClear}
        showClearButton
        blankValue
      />
    </Grid>
  );
};

const ReEntryOnlyBefore = () => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const algoRunning = useAlgoRunning();
  const modifyWhileLive = useModifyWhileLive();
  const loading = useAlgoLoading();

  const reEntryBefore = useSelector((state) => {
    const { reEntryBefore = '' } = getAlgoEditConfigConfigurations(state);

    return reEntryBefore;
  });

  const onChange = (val) => {
    dispatch(configurationDataUpdate('reEntryBefore', val));
  };

  const onClear = () => {
    dispatch(configurationDataUpdate('reEntryBefore', ''));
  };

  const [showError, helperText] = useAlgoValidationError(
    'configurations.reEntryBefore'
  );

  return (
    <Grid item xs={12} lg={4} className={classes.ReEntryOnlyBefore}>
      <TimeSelectorGeneral
        label="Re-entry / Re-execute only before (time):"
        name="reEntryBefore"
        value={reEntryBefore}
        error={showError}
        helperText={helperText}
        disabled={(algoRunning && !modifyWhileLive) || loading}
        onChange={onChange}
        onClear={onClear}
        showClearButton
        blankValue
      />
    </Grid>
  );
};

const ReEntryMaxCountLegs = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const algoRunning = useAlgoRunning();
  const loading = useAlgoLoading();
  const positionReEntryRepeatMode = useSelector((state) => {
    const { positionReEntryRepeatMode = RE_ENTRY_REPEAT_SEPARATE } =
      getAlgoEditConfigConfigurations(state);

    return positionReEntryRepeatMode;
  });

  const [showError, helperText] = useAlgoValidationError(
    'configurations.positionReEntryRepeatMode'
  );

  const onChange = (val) => {
    dispatch(configurationDataUpdate('positionReEntryRepeatMode', val));
  };

  const options = useMemo(
    () => [
      {
        value: RE_ENTRY_REPEAT_SEPARATE,
        label: 'Individual',
      },
      {
        value: RE_ENTRY_REPEAT_COMMON,
        label: 'Common',
      },
    ],
    []
  );

  return (
    <Grid item xs={12} lg={4} className={classes.ReEntryMaxCountLegs}>
      <ElevatedSelectControlled
        label="Re-entry max count (Individual legs):"
        disableClearable
        name="positionReEntryRepeatMode"
        error={showError}
        helperText={helperText}
        disabled={algoRunning || loading}
        value={positionReEntryRepeatMode}
        options={options}
        onChange={onChange}
      />
    </Grid>
  );
};

const ReEntryMaxCountMTM = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const algoRunning = useAlgoRunning();
  const loading = useAlgoLoading();
  const mtmReEntryRepeatMode = useSelector((state) => {
    const { mtmReEntryRepeatMode = RE_ENTRY_REPEAT_SEPARATE } =
      getAlgoEditConfigConfigurations(state);

    return mtmReEntryRepeatMode;
  });

  const [showError, helperText] = useAlgoValidationError(
    'configurations.mtmReEntryRepeatMode'
  );

  const onChange = (val) => {
    dispatch(configurationDataUpdate('mtmReEntryRepeatMode', val));
  };

  const options = useMemo(
    () => [
      {
        value: RE_ENTRY_REPEAT_SEPARATE,
        label: 'Individual',
      },
      {
        value: RE_ENTRY_REPEAT_COMMON,
        label: 'Common',
      },
    ],
    []
  );

  return (
    <Grid item xs={12} lg={4} className={classes.ReEntryMaxCountMTM}>
      <ElevatedSelectControlled
        label="Re-entry max count (MTM Target/Stoploss):"
        disableClearable
        name="mtmReEntryRepeatMode"
        error={showError}
        helperText={helperText}
        disabled={algoRunning || loading}
        value={mtmReEntryRepeatMode}
        options={options}
        onChange={onChange}
      />
    </Grid>
  );
};

const ReEntryAsapOrderDelay = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const algoRunning = useAlgoRunning();
  const loading = useAlgoLoading();

  const reEntryAsapOrderDelay = useSelector((state) => {
    const { reEntryAsapOrderDelay = 0 } =
      getAlgoEditConfigConfigurations(state);

    return reEntryAsapOrderDelay;
  });

  const [showError, helperText] = useAlgoValidationError(
    'configurations.reEntryAsapOrderDelay'
  );

  const [value, setValue] = useState(reEntryAsapOrderDelay);

  useEffect(() => {
    setValue(reEntryAsapOrderDelay);
  }, [reEntryAsapOrderDelay]);

  const onChange = (val) => {
    setValue(val);
  };

  const onBlur = () => {
    dispatch(configurationDataUpdate('reEntryAsapOrderDelay', value));
  };

  return (
    <BaseLimitOrderTimeoutSeconds
      onChange={onChange}
      onBlur={onBlur}
      disabled={algoRunning || loading}
      value={value}
      error={showError}
      helperText={helperText}
      label="Re-execute order delay (seconds):"
      name="reEntryAsapOrderDelay"
      className={classes.ReEntryAsapOrderDelay}
      placeholder="Seconds"
    />
  );
};

const ReEntryOrderPlacement = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const algoRunning = useAlgoRunning();
  const loading = useAlgoLoading();
  const reEntryOrderPlacement = useSelector((state) => {
    const { reEntryOrderPlacement = SL_ORDER_PLACEMENT_LATER } =
      getAlgoEditConfigConfigurations(state);

    return reEntryOrderPlacement;
  });

  const entryOrderType = useSelector((state) => {
    const { entryOrderType = ORDER_TYPE_M } =
      getAlgoEditConfigConfigurations(state);

    return entryOrderType;
  });

  const [showError, helperText] = useAlgoValidationError(
    'configurations.reEntryOrderPlacement'
  );

  const onChange = (val) => {
    dispatch(configurationDataUpdate('reEntryOrderPlacement', val));
  };

  const options = useMemo(
    () => [
      {
        value: SL_ORDER_PLACEMENT_ADVANCE,
        label: 'Place in advance',
      },
      {
        value: SL_ORDER_PLACEMENT_LATER,
        label: 'Place later',
      },
    ],
    []
  );

  return (
    <Grid item xs={12} lg={4} className={classes.ReEntryOrderPlacement}>
      <ElevatedSelectControlled
        label="Re-Entry/Re-Cost/Re-Execute Entry Order Placement:"
        disableClearable
        name="reEntryOrderPlacement"
        error={showError}
        helperText={helperText}
        disabled={algoRunning || loading || entryOrderType === ORDER_TYPE_M}
        value={reEntryOrderPlacement}
        options={options}
        onChange={onChange}
      />
    </Grid>
  );
};

const ReEntryActivationType = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const algoRunning = useAlgoRunning();
  const loading = useAlgoLoading();
  const reEntryReCostActType = useSelector((state) => {
    const { reEntryReCostActType = '' } =
      getAlgoEditConfigConfigurations(state);

    return reEntryReCostActType;
  });

  const [showError, helperText] = useAlgoValidationError(
    'configurations.reEntryReCostActType'
  );

  const onChange = (val) => {
    dispatch(configurationDataUpdate('reEntryReCostActType', val));
  };

  const options = useMemo(
    () => [
      {
        value: '',
        label: 'Next minute (Stockmock)',
      },
      {
        value: INTERVAL_ASAP,
        label: 'Immediate',
      },
    ],
    []
  );

  return (
    <Grid item xs={12} lg={4} className={classes.ReEntryActivationType}>
      <ElevatedSelectControlled
        label="Re-Cost Activation:"
        disableClearable
        name="reEntryReCostActType"
        error={showError}
        helperText={helperText}
        disabled={algoRunning || loading}
        value={reEntryReCostActType}
        options={options}
        onChange={onChange}
      />
    </Grid>
  );
};

const ReEntryAdvancedSettings = () => {
  const classes = useStyles();

  const isReEntryEnabled = useSelector((state) => {
    const { isReEntryEnabled = false } = getAlgoEditConfigConfigurations(state);

    return isReEntryEnabled;
  });

  return (
    <>
      <GridGap />
      {isReEntryEnabled && (
        <Grid item xs={12} className={classes.root}>
          <BubbleGridRow>
            <ReEntryType />
            <ReEntryOnlyAfter />
            <ReEntryOnlyBefore />
            <Grid item xs={12} className={classes.space} />
            <ReEntryMaxCountLegs />
            <ReEntryMaxCountMTM />
            <Grid item xs={12} className={classes.space} />
            <ReEntryAsapOrderDelay />
            <Grid item xs={12} className={classes.space} />
            <ReEntryOrderPlacement />
            <ReEntryActivationType />
          </BubbleGridRow>
        </Grid>
      )}
      <GridGap />
    </>
  );
};

export default ReEntryAdvancedSettings;
